import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  TranscriptEntryAdapter,
  TranscriptEntryResponseInterface,
} from '@modules/content-api/adapter/transcript-entry.adapter';
import { TranscriptResponseInterface } from '@modules/content-api/adapter/transcript.adapter';
import { ContentAPIService } from '@modules/content-api/content-api.service';
import { DataLayerService } from '@modules/shared/services/data-layer.service';
import { ANALYTIC_EVENTS, TrackingService, TRACKING_IDENTIFIER } from '@modules/shared/services/tracking.service';
import { take, map } from 'rxjs/operators';

import { TranscriptEntryModel } from '../models/transcript-entry.model';
import { TranscriptModel, TranscriptStatus } from '../models/transcript.model';

@Injectable({
  providedIn: 'root',
})
export class TranscriptService {
  public isLoadingEntries = false;

  private pollTimer: any = null;
  private includes = [
    'transcript_entries',
    'transcript_entries.chapter',
    'transcript_entries.speaker',
    'transcript_entries.livestream_media',
    'transcript_entries.livestream_media.file',
    'transcript_entries.livestream_media.audio',
    'transcript_entries.livestream_media.image',
    // needs to be checked
    'transcript_entries.livestream_media.adjusted_image',
    'transcript_entries.livestream_media.video',
    'transcript_entries.livestream_media.alternate_images',
  ].join();
  constructor(
    private apiService: ContentAPIService,
    private transcriptEntryAdapter: TranscriptEntryAdapter,
    private trackingService: TrackingService,
    private dataLayerService: DataLayerService
  ) {}

  private pollEntries(transcript: TranscriptModel) {
    this.stopPolling();
    this.pollTimer = setTimeout(() => {
      if (transcript.status !== TranscriptStatus.FINISHED) {
        this.loadEntries(transcript, false);
      }
    }, 10000);
  }

  getEntries(transcriptId: string, lang?: string) {
    let params = new HttpParams().appendAll({
      include: this.includes,
    });
    return this.apiService.getResource('transcripts', transcriptId, params, lang).pipe(
      map((d) => {
        const data = d as TranscriptResponseInterface;
        return data.transcript_entries.map(
          (entry) => new TranscriptEntryModel(this.transcriptEntryAdapter.parse(entry))
        );
      })
    );
  }

  public loadEntries(transcript: TranscriptModel, fromStart = true) {
    if (fromStart) {
      this.isLoadingEntries = true;
    }

    let params = new HttpParams().appendAll({
      include: [
        'speaker',
        'transcript',
        'livestream_media',
        'livestream_media.file',
        'livestream_media.audio',
        'livestream_media.image',
        'livestream_media.video',
        'livestream_media.alternate_images',
      ].join(),
      'fields[transcript]': 'id',
      'filter[transcript.id]': transcript.id,
      'page[limit]': 1000,
    });

    if (transcript.status !== TranscriptStatus.FINISHED) {
      params = params.appendAll({
        'filter[time_bigger][condition][path]': 'time',
        'filter[time_bigger][condition][operator]': '>=',
        'filter[time_bigger][condition][value]': fromStart ? 0 : transcript.lastTimeChecked,
      });
    }
    this.apiService
      .getResources('transcript_entries', params, transcript.lang)
      .pipe(
        take(1),
        map((entries: any) =>
          entries.map(
            (entry: TranscriptEntryResponseInterface) =>
              new TranscriptEntryModel(this.transcriptEntryAdapter.parse(entry, transcript.context))
          )
        ),
        map((d) => {
          if (fromStart) {
            this.trackingService.push(location.href, {
              events: ANALYTIC_EVENTS.TRANSCRIPT_LOADED,
              [TRACKING_IDENTIFIER.RESOURCE_LANGUAGE]: transcript.lang,
              [TRACKING_IDENTIFIER.RESOURCE_ID]: transcript.id,
            });
          }
          return d;
        })
      )
      .subscribe((entries: TranscriptEntryModel[]) => {
        transcript.lastTimeChecked = entries.reduce((a, b) => (a > b.time ? a : b.time), 0);
        if (entries.length) {
          transcript.addEntries(entries);
        }
        this.pollEntries(transcript);
        if (this.isLoadingEntries) {
          this.isLoadingEntries = false;
        }
      });
  }

  public stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }

  public changeLanguage(transcript: TranscriptModel, lang: string) {
    // stop polling (we restart polling with the new language)
    this.stopPolling();
    transcript.lang = lang;
    transcript.clearEntries();
    this.loadEntries(transcript);

    this.dataLayerService.pushEvent({
      event: 'transcriptLanguageChange',
      transcript: {
        id: transcript.id,
        streamId: transcript.context.id,
        title: transcript.title,
        availableLanguages: transcript.languages,
      },
      language: transcript.lang,
    });
  }
}
